/**
 * CMS Metadata History Query
 *
 * Single server-side query function for fetching stream metadata history (playlist)
 * from the Payload CMS metadata-history endpoint.
 *
 * Consumed two ways:
 * - Via `createAsync` for SSR + reactive updates (PlaylistBlock, useMetadataHistory hook)
 * - Via direct `await` in `createEffect` for isolated fetching (playlist-suche search)
 */

import { query } from "@solidjs/router";
import type {
  MetadataHistoryResponse,
  MetadataHistoryQueryParams,
  MetadataHistoryResult,
} from "~/types/metadataHistory";
import { env } from "~/config/env";
import { buildMetadataHistoryUrl } from "../queryUrls";
import { fetchFromCMS, CMSError } from "../queryUtils";
import { logger } from "~/lib/logger/logger";
import { evictIfFull, touchEntry } from "~/lib/cache/cappedMap";
import { createSingleFlight } from "~/lib/cache/singleFlight";

/**
 * In-memory server-side cache for metadata history.
 *
 * Metadata history is identical for all users viewing the same channel,
 * so we cache it to avoid per-user CMS requests during SSR.
 */
const CACHE_TTL_MS = 15_000;
const MAX_CACHE_SIZE = 500;
const metadataHistoryCache = new Map<string, { data: MetadataHistoryResult; expiresAt: number }>();
const loadOnce = createSingleFlight<string, MetadataHistoryResult>();

function buildCacheKey(params: MetadataHistoryQueryParams): string {
  // Include every effective parameter (including livestreamId) in both the
  // result cache and in-flight key, using the same encoding as the request.
  return buildMetadataHistoryUrl(params);
}

/**
 * Server-side query for fetching metadata history by channel
 *
 * Uses fetchFromCMS for timeout (8s) and circuit breaker protection.
 * Returns a Result wrapper so the playlist UI can show granular error states
 * without throwing (metadata failure should not crash the page).
 */
export const getMetadataHistoryQuery = query(
  async (params: MetadataHistoryQueryParams): Promise<MetadataHistoryResult> => {
    "use server";

    const cacheKey = buildCacheKey(params);
    const cached = metadataHistoryCache.get(cacheKey);
    if (cached && Date.now() < cached.expiresAt) {
      touchEntry(metadataHistoryCache, cacheKey);
      return cached.data;
    }

    return loadOnce(cacheKey, async () => {
      try {
        const url = `${env.cmsApiUrl}${cacheKey}`;
        const data = await fetchFromCMS<MetadataHistoryResponse>(url, {
          logPrefix: "[metadataHistory]",
          category: "metadata",
        });

        // 404 → fetchFromCMS returns null
        if (!data || !Array.isArray(data.entries)) {
          logger.warn("[metadataHistory] No data or malformed response");
          return { success: false, data: null, error: "No metadata history available" };
        }

        logger.info(`[metadataHistory] Received ${data.entries.length} entries`);

        const result: MetadataHistoryResult = { success: true, data, error: null };
        evictIfFull(metadataHistoryCache, MAX_CACHE_SIZE);
        metadataHistoryCache.set(cacheKey, { data: result, expiresAt: Date.now() + CACHE_TTL_MS });
        return result;
      } catch (error) {
        const isCMS = error instanceof CMSError;
        const message = isCMS
          ? `CMS ${error.reason}: ${error.message}`
          : `Unexpected error: ${error instanceof Error ? error.message : error}`;

        logger.error(`[metadataHistory] ${message}`);

        return {
          success: false,
          data: null,
          error: message,
          statusCode: isCMS ? error.status : undefined,
        };
      }
    });
  },
  "metadataHistory"
);
